import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { Auth } from "aws-amplify";
import { isTreezSSOEnabled, useAuth } from "auth";
import { useStoreContext } from "context";
import useMeta from "../../../hooks/useMeta.js";
import useNotification from "../../../hooks/components/useNotification";
import { QUERY_STORES_MENU_LIST } from "../../../../apollo/queries/stores";
import { StoreSelector as UiStoreSelector } from "@/ui";

const EMPTY_STORE_ID = "__no_store__";

const StoreSelector = () => {
    const notify = useNotification();
    const { updateStoreId } = useAuth();
    const { loading: contextLoading } = useStoreContext();
    const { user } = useMeta();
    const { loading, data } = useQuery(QUERY_STORES_MENU_LIST);
    const [isSwitchingStore, setIsSwitchingStore] = useState(false);

    const stores = data?.getStoreMenu ?? [];
    const options = useMemo(
        () => stores.map(value => ({ id: value.store_id, label: value.name })),
        [stores],
    );

    const selectedStoreId =
        user?.storeId || (options.length > 0 ? options[0].id : EMPTY_STORE_ID);
    const selectorOptions =
        options.length > 0
            ? options
            : [{ id: EMPTY_STORE_ID, label: "No stores", disabled: true }];

    const isLoading = Boolean(contextLoading || loading || user.loading);

    const handleSwitchError = () => {
        notify("Error switching store. Please try again.", "error");
        setIsSwitchingStore(false);
    };

    const changeStore = (accountId, storeId) => {
        if (!storeId || storeId === EMPTY_STORE_ID) {
            return;
        }

        if (isTreezSSOEnabled) {
            updateStoreId(storeId);
            return;
        }

        setIsSwitchingStore(true);

        Auth.currentAuthenticatedUser({ bypassCache: true })
            .then(authenticatedUser => {
                Auth.updateUserAttributes(authenticatedUser, {
                    "custom:ACCOUNT_ID": accountId,
                    "custom:STORE_ID": storeId,
                })
                    .then(() => {
                        Auth.currentAuthenticatedUser({
                            bypassCache: true,
                        }).then(() => {
                            window.location.reload();
                        });
                    })
                    .catch(() => {
                        handleSwitchError();
                    });
            })
            .catch(() => {
                handleSwitchError();
            });
    };

    const onValueChange = nextStoreId => {
        if (isLoading || isSwitchingStore) {
            return;
        }

        const selectedStore = stores.find(v => v.store_id === nextStoreId);
        if (!selectedStore || selectedStore.store_id === user?.storeId) {
            return;
        }

        changeStore(selectedStore.account_id, selectedStore.store_id);
    };

    let metaLabel = "No stores";
    if (options.length > 0) {
        metaLabel = "Current store";
    }

    return (
        <UiStoreSelector
            emptySearchMessage="No stores"
            loading={isLoading || isSwitchingStore}
            metaLabel={metaLabel}
            onValueChange={onValueChange}
            options={selectorOptions}
            value={selectedStoreId}
        />
    );
};

export default StoreSelector;
